import React, { useEffect, useState } from "react";
import { useFormContext, SubmitHandler } from "react-hook-form";
import { Row, Col, Button, Spinner } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { Table, Space, Drawer, Alert, Tooltip } from "antd";
import { submitBudgetData } from "./budgetSubmissionHelper";
import {
  EditOutlined,
  DeleteOutlined,
  PaperClipOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { Icon } from "../../../components/Component";
import moment from "moment";
import { useFormData } from "./FormDataContext"; // Import your context hook
import { Material, SelectOptions } from "./types"; // Import your types
import { nanoid } from "nanoid";
import { render } from "@testing-library/react";
import dayjs from "dayjs";
import { formatCurrency } from "../../../utils/Utils";
import supplierApis from "../../../api/master/supplier";
import budgetApis from "../../../api/master/budget";
import departments from "../../../api/master/departments";
import AddNewBudgetLineForm from "./AddNewBudgetLineForm";
import BudgetLineBulkUpload from "./BudgetLineBulkUpload";

const BudgetMaterials: React.FC<{ next: () => void; prev: () => void }> = (
  props: any
) => {
  const navigate = useNavigate();
  const { handleSubmit } = useFormContext();
  const { formData, updateFormData } = useFormData();

  const [dataSource, setDataSource] = useState<Material[]>([]);
  const [materialCategoryOptions, setMaterialCategoryOptions] = useState([]);
  const [budgetHeader, setBudgetHeader] = useState<any>();
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const [budgetCategoryOptions, setBudgetCategoryOptions] = useState<
    SelectOptions[]
  >([]);
  const [departmentOptions, setDepartmentOptions] = useState<SelectOptions[]>(
    []
  );
  const [showAddBudgetLineForm, setShowAddBudgetLineForm] = useState(false);
  const [currentEditMaterial, setCurrentEditMaterial] =
    useState<Material | null>(null);
  const [hasErrors, setHasErrors] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  // Fetch budget categories and departments for mapping
  const fetchBudgetCategories = async () => {
    try {
      const response = await budgetApis.fetchBudgetCategoryList();
      if (response.status) {
        const categories = response.response;
        if (Array.isArray(categories)) {
          const categoryOptions = categories.map((item: any) => ({
            label: item.budgetCategoryName,
            value: item.budgetCategoryId.toString(),
          }));
          setBudgetCategoryOptions(categoryOptions);
        }
      }
    } catch (error) {
      console.log("Error fetching budget categories:", error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const { data, status } = await departments.fetchDeptData();
      if (status) {
        const deptOptions = data.map((item: any) => ({
          label: item.deptName,
          value: item.deptID?.toString(),
        }));
        setDepartmentOptions(deptOptions);
      }
    } catch (error) {
      console.log("Error fetching departments:", error);
    }
  };

  useEffect(() => {
    fetchBudgetCategories();
    fetchDepartments();
  }, []);

  const columns = [
    {
      title: "Budget Line",
      dataIndex: "budgetLineName",
      key: "budgetLineName",
      fixed: "left" as const,
      width: 120,
    },
    {
      title: "Budget Line Description",
      dataIndex: "lineDescription",
      key: "lineDescription",
      // fixed: 'left' as const,
      width: 200,
    },
    {
      title: "Budget Category",
      dataIndex: "budgetCategoryId",
      key: "budgetCategoryId",
      width: 150,
      render: (text: any, record: any) => {
        // If custom budget category exists, show it
        if (record.customBudgetCategory) {
          return record.customBudgetCategory;
        }

        // Otherwise, find the category name from budgetCategoryId
        if (record.budgetCategoryId) {
          const category = budgetCategoryOptions.find(
            (option) => option.value === record.budgetCategoryId.toString()
          );
          return category?.label || "Not Available";
        }

        return "Not Available";
      },
    },
    {
      title: "Budget For",
      dataIndex: "budgetFor",
      key: "budgetFor",
      width: 150,
      render: (text: any) => text || "Not Available",
    },
    {
      title: "Budget Value",
      dataIndex: "budgetValue",
      key: "budgetValue",
      width: 150,
      render: (text: any) => {
        if (text === null || text === undefined) return "";
        const textStr = typeof text === "string" ? text : text.toString();
        const value = parseFloat(textStr.replace(/,/g, ""));
        return isFinite(value) ? formatCurrency(value, "INR") : textStr;
      },
    },
    {
      title: "Planned Start Date",
      dataIndex: "startDate",
      key: "startDate",
      width: 200,
      render: (text: any) => dayjs(text).format("DD-MMM-YYYY"),
    },
    {
      title: "Contractor",
      dataIndex: "contractorName",
      key: "contractorName",
      width: 150,
      render: (text: any) => text || "-",
    },
    {
      title: "Planned End Date",
      dataIndex: "endDate",
      key: "endDate",
      width: 150,
      render: (text: any) => dayjs(text).format("DD-MMM-YYYY"),
    },
    {
      title: "Bill Of Material (BOM)",
      dataIndex: "bom",
      key: "bom",
      width: 150,
    },
    {
      title: "BOM Allocation",
      dataIndex: "allocation",
      key: "allocation",
      width: 150,
    },
    {
      title: "Department",
      dataIndex: "departmentName",
      key: "departmentName",
      width: 150,
    },
    {
      title: "Status",
      dataIndex: "errorFlag",
      key: "errorFlag",
      width: 80,
      align: "center" as const,
      render: (errorFlag: boolean, record: any) => {
        if (errorFlag && record.errorMessage) {
          const errorMessages = Array.isArray(record.errorMessage)
            ? record.errorMessage
            : [record.errorMessage];

          const tooltipContent = (
            <div style={{ maxWidth: 300 }}>
              <div style={{ fontWeight: 'bold', marginBottom: 8, color: '#ff4d4f' }}>
                Validation Errors:
              </div>
              {errorMessages.map((error: string, index: number) => (
                <div key={index} style={{ marginBottom: 4 }}>
                  • {error}
                </div>
              ))}
            </div>
          );

          return (
            <Tooltip title={tooltipContent} placement="topLeft">
              <ExclamationCircleOutlined
                style={{
                  color: '#ff4d4f',
                  fontSize: 18,
                  cursor: 'pointer'
                }}
              />
            </Tooltip>
          );
        }

        return (
          <Tooltip title="No validation errors">
            <CheckCircleOutlined
              style={{
                color: '#52c41a',
                fontSize: 18
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right" as const,
      width: 100,
      render: (_: any, record: Material) => (
        <Space size="middle">
          <a onClick={() => openForm(record)}>
            <EditOutlined />
          </a>
          <a onClick={() => handleDelete(record.key)} className="delete">
            <DeleteOutlined />
          </a>
        </Space>
      ),
    },
  ];

  const submitForm: SubmitHandler<any> = (data) => {
    props.next();
  };

  const handleSaveAsDraft = async () => {
    setIsSavingDraft(true);
    try {
      await submitBudgetData({
        formData,
        status: "DRAFT",
        navigate,
      });
    } catch (error) {
      console.error("Error saving as draft:", error);
    } finally {
      setIsSavingDraft(false);
    }
  };

  useEffect(() => {
    console.log("Previous Data:", formData);
    setBudgetHeader(formData.budgetHeader);
  }, [formData]);

  // Sync FormDataContext materialsData with local component state
  useEffect(() => {
    if (formData.materialsData) {
      const convertedMaterials: Material[] = formData.materialsData.map(
        (item: any) => ({
          ...item,
          status: item.status || "Draft",
          budgetId: item.budgetId || "",
          budgetLineId: item.budgetLineId || "",
          budgetValue:
            typeof item.budgetValue === "string"
              ? parseFloat(item.budgetValue.replace(/,/g, "")) || 0
              : item.budgetValue || 0,
        })
      );
      setDataSource(convertedMaterials);

      // Check if any materials have errors
      const hasErrorFlag = convertedMaterials.some(
        (item: any) => item.errorFlag === true
      );
      setHasErrors(hasErrorFlag);
    } else {
      setDataSource([]);
      setHasErrors(false);
    }
  }, [formData.materialsData]);

  const openForm = (material?: Material) => {
    setCurrentEditMaterial(material ?? null);
    setShowAddBudgetLineForm(true);
  };

  const closeForm = () => {
    setCurrentEditMaterial(null);
    setShowAddBudgetLineForm(false);
  };

  const handleSaveBudgetLine = (material: any) => {
    console.log("Saved Budget Line:", material);
    // FormDataContext is already updated in the form component
    closeForm();
  };

  const handleDelete = (key: string) => {
    const updatedMaterials = (formData.materialsData || []).filter(
      (material: any) => material.key !== key
    );
    updateFormData("materialsData", updatedMaterials);
  };

  const handleBulkUpload = (uploadedBudgetLines: any[]) => {
    // Get existing materials data or empty array
    const existingMaterials = formData.materialsData || [];

    // Add all uploaded budget lines to existing materials
    const updatedMaterials = [...existingMaterials, ...uploadedBudgetLines];

    // Update the form data context
    updateFormData("materialsData", updatedMaterials);
  };

  const handleExpand = (expanded: boolean, record: any) => {
    console.log("record", record, expanded);
    if (expanded) {
      // Add the record's key to the expandedRowKeys array
      setExpandedRowKeys((prevKeys) => [...prevKeys, record.key]);
    } else {
      // Remove the record's key from the expandedRowKeys array
      setExpandedRowKeys((prevKeys) =>
        prevKeys.filter((key) => key !== record.key)
      );
    }
  };

  const fetchCategorys = async () => {
    try {
      const { data, status } = await supplierApis.fetchSupplierCategory();
      if (status) {
        const categoryOption = data.map((item: any) => ({
          label: item.category,
          value: item.id.toString(),
        }));
        setMaterialCategoryOptions(categoryOption);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchCategorys();
  }, []);

  return (
    <>
      <form onSubmit={handleSubmit(submitForm)} style={{ maxWidth: "100%", minWidth: 0, width: "100%" }}>
        <div style={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
          {hasErrors && (
            <Alert
              message="Validation Errors Found"
              description="Some budget lines have validation errors. Please review and fix all errors before proceeding to the next step."
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          <Table
            columns={columns}
            dataSource={dataSource}
            scroll={{ x: "max-content" }}
            expandable={{
              expandedRowRender: (record: any) => {
                if (record.bom == "Y") {
                  return (
                    <div>
                      <div>
                        <p style={{ margin: 0 }}>
                          <strong>BOM Lines</strong>
                        </p>
                        <div className="mt-3">
                          <Table
                            className="customTable"
                            columns={[
                              {
                                title: "Category",
                                dataIndex: "categoryName",
                                key: "categoryName",
                                fixed: "left" as const,
                                width: 130,
                              },
                              {
                                title: "Material Name",
                                dataIndex: "materialName",
                                key: "materialName",
                                fixed: "left" as const,
                                width: 130,
                              },
                              {
                                title: "Qty",
                                dataIndex: "quantity",
                                key: "quantity",
                                fixed: "left" as const,
                                width: 130,
                              },
                              {
                                title: "UOM",
                                dataIndex: "uomName",
                                key: "uomName",
                                width: 130,
                              },
                              {
                                title: "Allocation",
                                dataIndex: "allocated",
                                key: "allocated",
                                width: 130,
                                render: (text: any) => {
                                  console.log("record", record);
                                  if (record.allocation === "value") {
                                    const value = parseFloat(
                                      text.toString().replace(/,/g, "")
                                    );

                                    // Check if the parsed value is a valid number
                                    if (!isNaN(value)) {
                                      // Format the value as currency
                                      return formatCurrency(value, "INR");
                                    } else {
                                      return text; // Return original value if invalid
                                    }
                                  } else {
                                    return text + "%";
                                  }
                                },
                              },
                            ]}
                            dataSource={record.bomLines?.map((item: any) => ({
                              ...item,
                              key: item?.id,
                            }))}
                            scroll={{ x: 1000 }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                }
                return null;
              },
              expandedRowKeys,
              onExpand: handleExpand,
              rowExpandable: (record: any) => record.bom === "Y", // Conditionally show expandable icon
            }}
            bordered
            size="small" // Making the table compa ct
            pagination={false}
            locale={{
              emptyText:
                "Please click on Add New Budget Line to add Budget lines.",
            }}
          />
          <div
            style={{
              marginTop: 10,
              display: "flex",
              gap: "15px",
              alignItems: "center",
            }}
          >
            <span
              onClick={() => {
                openForm();
              }}
              style={{
                cursor: "pointer",
                fontSize: 14,
              }}
            >
              <Icon name="plus-round"></Icon> &nbsp; Add New Budget Line
            </span>

            <BudgetLineBulkUpload
              onUpload={handleBulkUpload}
              budgetCategoryOptions={budgetCategoryOptions}
              departmentOptions={departmentOptions}
              budgetStartDate={budgetHeader?.startDate}
              budgetEndDate={budgetHeader?.endDate}
              siteId={budgetHeader?.site}
            />
          </div>
        </div>

        <Drawer
          title={
            currentEditMaterial ? "Edit Budget Line" : "Add New Budget Line"
          }
          placement="right"
          onClose={closeForm}
          open={showAddBudgetLineForm}
          width={1200}
          destroyOnClose={true}
        >
          <AddNewBudgetLineForm
            budgetStartDate={budgetHeader?.startDate}
            budgetEndDate={budgetHeader?.endDate}
            materialCategoryOptions={materialCategoryOptions}
            currentMaterial={currentEditMaterial}
            onSave={handleSaveBudgetLine}
            onCancel={closeForm}
          />
        </Drawer>
        <div className="actions clearfix">
          <ul>
            <li>
              <Button
                title={
                  dataSource.length === 0
                    ? "Please add materials"
                    : hasErrors
                    ? "Please fix all errors before proceeding"
                    : ""
                }
                disabled={dataSource.length === 0 || hasErrors}
                color="primary"
                type="submit"
              >
                Next
              </Button>
            </li>
             <li>
              <Button
                color="primary"
                onClick={props.prev}
                className="btn-dim btn btn-secondary"
              >
                Previous
              </Button>
            </li>
            <li>
              {isSavingDraft ? (
                <Button color="secondary" type="button">
                  <Spinner size="sm" color="light" />
                </Button>
              ) : (
                <Button
                  color="secondary"
                  onClick={handleSaveAsDraft}
                  disabled={dataSource.length === 0 || hasErrors}
                  title={
                    dataSource.length === 0
                      ? "Please add materials"
                      : hasErrors
                      ? "Please fix all errors before saving"
                      : ""
                  }
                >
                  Save as Draft
                </Button>
              )}
            </li>
           
          </ul>
        </div>
      </form>
    </>
  );
};

export default React.memo(BudgetMaterials);
